/**
 * The wizard's first step, Upload (docs/plans/lms-course-builder-plan.md 8.2
 * step 1, 6.2, 7.3, 9.2, 18.2 Q3 and Q7; WP-B30): the files (PDF, DOCX, TXT
 * and MD, twelve at most with the web links, 50 MB each: the config route's
 * limits are the live values), an optional title and public address per file,
 * web links as sources (https only; the worker downloads each once, the api
 * never fetches on the request), citations without a file (a title, a
 * publisher and an address; no text is read from them), the running count
 * against the category's source minimum, and the policy notice with the
 * attestation checkbox the server requires (400 LMS_BUILDER_ATTESTATION_REQUIRED
 * otherwise).
 *
 * The step holds no state of its own beyond the two draft fields of the "add
 * a link" and "add a citation" rows: BuildWizard owns the lists, so going
 * back and forth between steps keeps them.
 */
import { useId, useRef, useState } from 'react';
import { FileText, Link2, Plus, Quote, ShieldAlert, Trash2, Upload } from 'lucide-react';
import { iconBtnCls, outlineBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import { BUILDER_FILE_ACCEPT } from '../../../../api/builder';
import type { CourseCategory } from '../../../../content/types';
import { noticeCls } from '../../../../components/player/styles';
import { Checkbox, TextInput } from '../fields';
import { cardCls, hintCls, labelCls, subheadingCls } from '../styles';
import {
  ATTESTATION_LABEL,
  builderFileProblem,
  citationProblem,
  fileSizeLabel,
  linkProblem,
  sourceCountLine,
  UPLOAD_POLICY_TEXT,
} from './runModel';

/** One chosen file with the admin's optional facts about it (sources_meta). */
export interface UploadEntry {
  file: File;
  title: string;
  publicUrl: string;
}

/** One web link (source_links). */
export interface LinkEntry {
  url: string;
  title: string;
}

/** One citation without a file (extra_sources). */
export interface CitationEntry {
  title: string;
  publisher: string;
  url: string;
}

export interface UploadStepProps {
  files: UploadEntry[];
  onFiles: (files: UploadEntry[]) => void;
  links: LinkEntry[];
  onLinks: (links: LinkEntry[]) => void;
  citations: CitationEntry[];
  onCitations: (citations: CitationEntry[]) => void;
  attested: boolean;
  onAttested: (attested: boolean) => void;
  /** Files plus links, at most (config limits.max_files). */
  maxFiles: number;
  /** Bytes per file, at most (config limits.max_bytes_per_file). */
  maxBytes: number;
  /** 7.3: the category's source minimum. */
  minimum: number;
  category: CourseCategory | null;
}

/** The problems that keep the step from moving on (empty when it can). */
export function uploadProblems(props: Pick<UploadStepProps, 'files' | 'links' | 'citations' | 'attested' | 'maxFiles' | 'maxBytes'>): string[] {
  const problems: string[] = [];
  const count = props.files.length + props.links.length;
  if (count === 0) problems.push('Add at least one file or web link to build from.');
  if (count > props.maxFiles) problems.push(`A run takes at most ${props.maxFiles} files and web links together.`);
  if (props.files.some((f) => builderFileProblem(f.file, props.maxBytes))) problems.push('Remove or replace the files marked below.');
  if (props.files.some((f) => f.publicUrl.trim() && !/^https:\/\/\S+$/i.test(f.publicUrl.trim()))) {
    problems.push('A public address must start with https://.');
  }
  if (props.links.some((l) => linkProblem(l.url))) problems.push('Fix the web links marked below.');
  if (props.citations.some((c) => citationProblem(c))) problems.push('Complete or remove the citations marked below.');
  if (!props.attested) problems.push(`Tick "${ATTESTATION_LABEL}".`);
  return problems;
}

export function UploadStep({
  files,
  onFiles,
  links,
  onLinks,
  citations,
  onCitations,
  attested,
  onAttested,
  maxFiles,
  maxBytes,
  minimum,
  category,
}: UploadStepProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [linkDraft, setLinkDraft] = useState<LinkEntry>({ url: '', title: '' });
  const [linkError, setLinkError] = useState<string | null>(null);
  const [citationDraft, setCitationDraft] = useState<CitationEntry>({ title: '', publisher: '', url: '' });
  const [citationError, setCitationError] = useState<string | null>(null);

  const used = files.length + links.length;
  const sourceCount = used + citations.length;
  const maxMb = Math.round(maxBytes / (1024 * 1024));

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const next = [...files];
    Array.from(list).forEach((file) => next.push({ file, title: '', publicUrl: '' }));
    onFiles(next);
    if (inputRef.current) inputRef.current.value = '';
  };

  const updateFile = (index: number, patch: Partial<UploadEntry>) =>
    onFiles(files.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  const addLink = () => {
    const problem = linkProblem(linkDraft.url);
    if (problem) {
      setLinkError(problem);
      return;
    }
    if (links.some((l) => l.url.trim() === linkDraft.url.trim())) {
      setLinkError('That address is already listed.');
      return;
    }
    onLinks([...links, { url: linkDraft.url.trim(), title: linkDraft.title.trim() }]);
    setLinkDraft({ url: '', title: '' });
    setLinkError(null);
  };

  const addCitation = () => {
    const problem = citationProblem(citationDraft);
    if (problem) {
      setCitationError(problem);
      return;
    }
    onCitations([...citations, { title: citationDraft.title.trim(), publisher: citationDraft.publisher.trim(), url: citationDraft.url.trim() }]);
    setCitationDraft({ title: '', publisher: '', url: '' });
    setCitationError(null);
  };

  return (
    <div className="space-y-4" data-testid="lms-builder-upload">
      <div className={noticeCls('amber')} role="note" data-testid="lms-builder-policy">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>{UPLOAD_POLICY_TEXT}</span>
      </div>

      <section className={`${cardCls} space-y-3`} aria-labelledby={`${inputId}-files`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id={`${inputId}-files`} className={`flex items-center gap-2 ${subheadingCls}`}>
            <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Files
          </h3>
          <span className={hintCls} data-testid="lms-builder-used">{`${used} of ${maxFiles} files and web links`}</span>
        </div>
        <div>
          <label htmlFor={inputId} className={labelCls}>
            {`Add files (PDF, Word, text or Markdown; up to ${maxFiles} files and web links together, ${maxMb} MB each)`}
          </label>
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            multiple
            accept={BUILDER_FILE_ACCEPT}
            data-testid="lms-builder-file-input"
            className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:min-h-[44px] file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200 sm:file:min-h-0"
            onChange={(e) => addFiles(e.target.files)}
          />
          <p className={`${hintCls} mt-1`}>PowerPoint is not read: export a deck to PDF. Scanned pages with no text layer are refused when the files are read.</p>
        </div>
        {files.length > 0 ? (
          <ul className="space-y-3" data-testid="lms-builder-files">
            {files.map((entry, i) => {
              const problem = builderFileProblem(entry.file, maxBytes);
              return (
                <li
                  key={`${entry.file.name}-${i}`}
                  className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  data-testid={`lms-builder-file-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{entry.file.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{`${i + 1} \u00b7 ${fileSizeLabel(entry.file.size)}`}</div>
                      {problem ? (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-400" data-testid={`lms-builder-file-error-${i}`}>
                          {problem}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => onFiles(files.filter((_, j) => j !== i))}
                      aria-label={`Remove ${entry.file.name}`}
                      data-testid={`lms-builder-file-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <TextInput
                      label="Title in the sources list (optional)"
                      value={entry.title}
                      onChange={(title) => updateFile(i, { title })}
                      placeholder={entry.file.name}
                      testId={`lms-builder-file-title-${i}`}
                    />
                    <TextInput
                      label="Public address of the document (optional)"
                      type="url"
                      value={entry.publicUrl}
                      onChange={(publicUrl) => updateFile(i, { publicUrl })}
                      placeholder="https://"
                      testId={`lms-builder-file-url-${i}`}
                      hint="Without one, the course links to the uploaded file."
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      <section className={`${cardCls} space-y-3`} aria-labelledby={`${inputId}-links`}>
        <h3 id={`${inputId}-links`} className={`flex items-center gap-2 ${subheadingCls}`}>
          <Link2 className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Web links
        </h3>
        <p className={hintCls}>
          A web page or a document on a public site, read as a source like a file. The builder downloads each address once when it reads the files; it does not follow links on the page.
        </p>
        {links.length > 0 ? (
          <ul className="space-y-1" data-testid="lms-builder-links">
            {links.map((link, i) => (
              <li key={link.url} className="flex items-center justify-between gap-2 text-sm" data-testid={`lms-builder-link-${i}`}>
                <span className="min-w-0 truncate text-gray-800 dark:text-gray-200">
                  {link.title ? `${link.title} \u00b7 ` : ''}
                  <span className="font-mono text-xs">{link.url}</span>
                </span>
                <button
                  type="button"
                  className={iconBtnCls}
                  onClick={() => onLinks(links.filter((_, j) => j !== i))}
                  aria-label={`Remove ${link.url}`}
                  data-testid={`lms-builder-link-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
          <TextInput
            label="Address (https)"
            type="url"
            value={linkDraft.url}
            onChange={(url) => {
              setLinkDraft({ ...linkDraft, url });
              setLinkError(null);
            }}
            placeholder="https://www.ecfr.gov/..."
            testId="lms-builder-link-url"
          />
          <TextInput
            label="Title (optional)"
            value={linkDraft.title}
            onChange={(title) => setLinkDraft({ ...linkDraft, title })}
            testId="lms-builder-link-title"
          />
          <button type="button" className={secondaryBtnCls} onClick={addLink} data-testid="lms-builder-link-add">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add link
          </button>
        </div>
        {linkError ? (
          <p className="text-xs text-red-700 dark:text-red-400" role="alert" data-testid="lms-builder-link-error">
            {linkError}
          </p>
        ) : null}
      </section>

      <details className={`${cardCls} group`} open={citations.length > 0}>
        <summary className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 sm:min-h-0">
          <Quote className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          {`Citations without a file (${citations.length})`}
        </summary>
        <div className="mt-3 space-y-3">
          <p className={hintCls}>A citation is listed in the course sources and counts toward the minimum, but no text is read from it.</p>
          {citations.length > 0 ? (
            <ul className="space-y-1" data-testid="lms-builder-citations">
              {citations.map((c, i) => (
                <li key={`${c.url}-${i}`} className="flex items-center justify-between gap-2 text-sm" data-testid={`lms-builder-citation-${i}`}>
                  <span className="min-w-0 truncate text-gray-800 dark:text-gray-200">{`${c.title} \u00b7 ${c.publisher}`}</span>
                  <button
                    type="button"
                    className={iconBtnCls}
                    onClick={() => onCitations(citations.filter((_, j) => j !== i))}
                    aria-label={`Remove ${c.title}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-3">
            <TextInput
              label="Title"
              value={citationDraft.title}
              onChange={(title) => setCitationDraft({ ...citationDraft, title })}
              testId="lms-builder-citation-title"
            />
            <TextInput
              label="Publisher"
              value={citationDraft.publisher}
              onChange={(publisher) => setCitationDraft({ ...citationDraft, publisher })}
              testId="lms-builder-citation-publisher"
            />
            <TextInput
              label="Address (https)"
              type="url"
              value={citationDraft.url}
              onChange={(url) => setCitationDraft({ ...citationDraft, url })}
              testId="lms-builder-citation-url"
            />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" className={outlineBtnCls()} onClick={addCitation} data-testid="lms-builder-citation-add">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add citation
            </button>
            {citationError ? (
              <span className="text-xs text-red-700 dark:text-red-400" role="alert">
                {citationError}
              </span>
            ) : null}
          </div>
        </div>
      </details>

      <div className="space-y-2">
        <p
          className={`text-sm ${sourceCount >= minimum ? 'text-gray-700 dark:text-gray-300' : 'text-amber-800 dark:text-amber-300'}`}
          data-testid="lms-builder-source-count"
          aria-live="polite"
        >
          <Upload className="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden="true" />
          {sourceCountLine(sourceCount, minimum, category)}
        </p>
        <Checkbox label={ATTESTATION_LABEL} checked={attested} onChange={onAttested} testId="lms-builder-attest" />
      </div>
    </div>
  );
}

export default UploadStep;
